// src/pages/admin/AdminNegocios.js

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Table, TableHead, TableRow, TableCell,
  TableBody, TablePagination, Chip, IconButton, Stack, Alert, CircularProgress, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Button, Divider, Grid, Snackbar,
} from '@mui/material';
import BlockIcon         from '@mui/icons-material/Block';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import CancelIcon        from '@mui/icons-material/Cancel';
import PlayCircleIcon    from '@mui/icons-material/PlayCircle';
import VisibilityIcon    from '@mui/icons-material/Visibility';
import KeyIcon           from '@mui/icons-material/Key';
import CardGiftcardIcon  from '@mui/icons-material/CardGiftcard';
import PaidIcon          from '@mui/icons-material/Paid';
import AssignmentIcon    from '@mui/icons-material/Assignment';
import HistoryIcon       from '@mui/icons-material/History';
import SearchIcon        from '@mui/icons-material/Search';
import ClearIcon         from '@mui/icons-material/Clear';
import { admin } from '../../api/authApi';
import ChipEstadoCuenta from '../../components/admin/ChipEstadoCuenta';
import DialogCambiarPassword from '../../components/admin/DialogCambiarPassword';
import DialogActivarSinPago from '../../components/admin/DialogActivarSinPago';
import DialogHistorialAuditoria from '../../components/admin/DialogHistorialAuditoria';
import { fechaHoraLocal } from '../../utils/format';
import {
  useFiltrosUbicacion, SelectorDepartamento, SelectorMunicipio, SelectorGuia,
} from '../../components/admin/FiltrosUbicacion';

const ESTADOS = [
  { v: '',            label: 'Todos' },
  { v: 'activo',      label: 'Activos' },
  { v: 'suspendido',  label: 'Suspendidos' },
  { v: 'pendiente_aprobacion', label: 'Pendientes de aprobación' },
  { v: 'rechazado',   label: 'Rechazados' },
  { v: 'demo',        label: 'Demo' },
  { v: 'inactivo',    label: 'Inactivos' },
];

const colorEstado = (e) => ({
  activo: 'success', suspendido: 'error', rechazado: 'error',
  pendiente_aprobacion: 'warning',
  demo: 'info', inactivo: 'default', cancelado: 'default',
}[e] || 'default');

// El backend guarda el estado con guion bajo; la tabla lo muestra legible.
const etiquetaEstado = (e) => ({
  pendiente_aprobacion: 'Pendiente',
  activo: 'Activo', suspendido: 'Suspendido', rechazado: 'Rechazado',
  demo: 'Demo', inactivo: 'Inactivo', cancelado: 'Cancelado',
}[e] || e);

// D-26: el estado de cobro es distinto del estado del tenant. Un negocio
// puede seguir "activo" en la base y estar bloqueado por mora, porque el
// acceso se corta por fecha sin esperar al ciclo de cobro.
const chipCobro = (n) => {
  switch (n.estadoCobro) {
    case 'EN_MORA':
      return <Chip size="small" color="error"
                   label={`En mora${n.diasMora ? ` · ${n.diasMora} d` : ''}`} />;
    case 'PENDIENTE_PAGO':  return <Chip size="small" color="warning" label="Pendiente" />;
    case 'AL_DIA':          return <Chip size="small" color="success" label="Al día" />;
    case 'EXENTA':          return <Chip size="small" color="info" label="Demo · sin cobro" />;
    case 'SIN_PRIMER_PAGO': return <Chip size="small" variant="outlined" label="Sin primer pago" />;
    case 'SIN_SUSCRIPCION': return <Chip size="small" variant="outlined" label="Sin suscripción" />;
    default:
      return <Chip size="small" variant="outlined" label={n.estadoCobro || '—'} />;
  }
};

export default function AdminNegocios() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [estado, setEstado] = useState('');
  const [filtrosGeo, setFiltrosGeo] = useState({ departamentoId: '', municipioId: '', guiaId: '' });
  const [page, setPage]     = useState(0);
  const [size, setSize]     = useState(20);

  const geo = useFiltrosUbicacion({ cargarGuias: true });

  const limpiarFiltros = () => {
    setSearch('');
    setEstado('');
    setFiltrosGeo({ departamentoId: '', municipioId: '', guiaId: '' });
    setPage(0);
  };
  const hayFiltros = !!(search || estado || filtrosGeo.departamentoId
                        || filtrosGeo.municipioId || filtrosGeo.guiaId);

  const [data, setData] = useState({ total: 0, items: [] });
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [password, setPassword] = useState(null);  // null | negocio (cuenta del dueño)
  const [sinPago, setSinPago]   = useState(null);  // null | negocio (D-26)
  const [historial, setHistorial] = useState(null); // null | negocio (D-27)
  const [aviso, setAviso] = useState('');
  const [detalle, setDetalle] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const r = await admin.negociosListar({
        search: search || undefined,
        estado: estado || undefined,
        departamentoId: filtrosGeo.departamentoId || undefined,
        municipioId:    filtrosGeo.municipioId    || undefined,
        guiaId:         filtrosGeo.guiaId         || undefined,
        page:   page + 1, size,
      });
      setData({ total: r.total, items: r.items });
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudieron cargar los negocios.');
    } finally { setCargando(false); }
  }, [search, estado, filtrosGeo, page, size]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirDetalle = async (id) => {
    try { setDetalle(await admin.negocioDetalle(id)); }
    catch (e) { setError(e.mensajeUsuario || 'No se pudo cargar el detalle.'); }
  };

  const suspender = async (id) => {
    const motivo = prompt('Motivo de la suspensión:');
    if (!motivo) return;
    try { await admin.negocioSuspender(id, motivo); if (detalle?.TenantID === id) setDetalle(await admin.negocioDetalle(id)); cargar(); }
    catch (e) { alert(e.mensajeUsuario || 'No se pudo suspender.'); }
  };
  // Un negocio nace en pendiente_aprobacion y NO aparece en el directorio ni
  // se le puede pedir hasta que se apruebe aquí. El botón no es en sí un
  // control de seguridad: lo que blinda es que nazca cerrado. Esto es
  // simplemente cómo se abre, y alcanza mientras el piloto sean los negocios
  // conocidos de una zona.
  const aprobar = async (id, nombre) => {
    if (!window.confirm(
      `¿Aprobar "${nombre}"?\n\n` +
      'Va a aparecer en el directorio del barrio y los vecinos van a poder ' +
      'pedirle, entregándole su dirección exacta. Apruébalo solo si sabes ' +
      'que el negocio existe de verdad.')) return;
    try { await admin.negocioAprobar(id); if (detalle?.TenantID === id) setDetalle(await admin.negocioDetalle(id)); cargar(); }
    catch (e) { alert(e.mensajeUsuario || 'No se pudo aprobar.'); }
  };

  const rechazar = async (id) => {
    const motivo = prompt('Motivo del rechazo:');
    if (!motivo) return;
    try { await admin.negocioRechazar(id, motivo); if (detalle?.TenantID === id) setDetalle(await admin.negocioDetalle(id)); cargar(); }
    catch (e) { alert(e.mensajeUsuario || 'No se pudo rechazar.'); }
  };

  // D-26: quitar la exención de una cuenta demo; vuelve al cobro normal
  // en el próximo corte.
  const reanudarCobro = async (n) => {
    if (!window.confirm(
      `¿Reanudar el cobro de "${n.nombre}"?\n\n` +
      'Deja de ser cuenta demo: en el próximo corte se le genera cuenta de cobro ' +
      'y, si no paga, se suspende como cualquier otro negocio.')) return;
    const motivo = window.prompt('Motivo (opcional):') || '';
    try {
      const r = await admin.negocioReanudarCobro(n.tenantID, motivo);
      setAviso(r?.mensaje || 'Cobro reanudado.');
      cargar();
    } catch (e) { setAviso(''); alert(e.mensajeUsuario || 'No se pudo reanudar el cobro.'); }
  };

  const reactivar = async (id) => {
    try { await admin.negocioReactivar(id); if (detalle?.TenantID === id) setDetalle(await admin.negocioDetalle(id)); cargar(); }
    catch (e) { alert(e.mensajeUsuario || 'No se pudo reactivar.'); }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontFamily: 'Fraunces', mb: 3 }}>Negocios</Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap"
                 useFlexGap sx={{ rowGap: 1.5 }}>
            <TextField label="Buscar (nombre, correo o slug)"
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              size="small" sx={{ flex: '1 1 260px', minWidth: 260 }}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }} />

            <TextField select label="Estado" value={estado}
              onChange={(e) => { setEstado(e.target.value); setPage(0); }}
              size="small" sx={{ width: 180 }}>
              {ESTADOS.map((e) => <MenuItem key={e.v} value={e.v}>{e.label}</MenuItem>)}
            </TextField>

            <SelectorDepartamento geo={geo} valor={filtrosGeo.departamentoId}
              onChange={(v) => { setFiltrosGeo(f => ({ ...f, departamentoId: v, municipioId: '' })); setPage(0); }} />
            <SelectorMunicipio geo={geo} valor={filtrosGeo.municipioId}
              departamentoId={filtrosGeo.departamentoId}
              onChange={(v) => { setFiltrosGeo(f => ({ ...f, municipioId: v })); setPage(0); }} />
            <SelectorGuia geo={geo} valor={filtrosGeo.guiaId}
              onChange={(v) => { setFiltrosGeo(f => ({ ...f, guiaId: v })); setPage(0); }} />

            {hayFiltros && (
              <Button size="small" startIcon={<ClearIcon />} onClick={limpiarFiltros}
                      sx={{ ml: 'auto' }}>
                Limpiar
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Correo</TableCell>
              <TableCell>Sucursal</TableCell>
              <TableCell>Municipio</TableCell>
              <TableCell>Guía</TableCell>
              <TableCell align="right">Productos</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Cobro</TableCell>
              <TableCell>Cuenta del representante</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cargando && <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>}
            {!cargando && data.items.length === 0 && (
              <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                Aún no hay negocios inscritos.
              </TableCell></TableRow>)}
            {!cargando && data.items.map((n) => (
              <TableRow key={n.tenantID} hover>
                <TableCell>{n.nombre}</TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{n.slug}</Typography></TableCell>
                <TableCell>{n.email || '—'}</TableCell>
                <TableCell>{n.sucursalNombre || '—'}</TableCell>
                <TableCell>
                  {n.municipioNombre
                    ? <>
                        <Typography variant="body2">{n.municipioNombre}</Typography>
                        <Typography variant="caption" color="text.secondary">{n.departamentoNombre}</Typography>
                      </>
                    : '—'}
                </TableCell>
                <TableCell>
                  {n.guiaNombre
                    ? <Chip size="small" label={n.guiaNombre} variant="outlined" color="primary" />
                    : <Typography variant="caption" color="text.secondary">Sin guía</Typography>}
                </TableCell>
                <TableCell align="right">{n.productosCount ?? 0}</TableCell>
                <TableCell><Chip size="small" label={etiquetaEstado(n.estado)} color={colorEstado(n.estado)} /></TableCell>
                <TableCell>{chipCobro(n)}</TableCell>
                <TableCell>
                  <ChipEstadoCuenta
                    estado={n.estadoCuentaOwner}
                    correoConfirmado={n.ownerCorreoConfirmado}
                    bloqueado={n.ownerBloqueado}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Ver detalle"><IconButton size="small" onClick={() => abrirDetalle(n.tenantID)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title={n.exentaDeCobro
                    ? 'Reanudar el cobro normal'
                    : 'Activar sin pago (cuenta demo)'}>
                    <IconButton size="small"
                      color={n.exentaDeCobro ? 'default' : 'secondary'}
                      onClick={() => (n.exentaDeCobro ? reanudarCobro(n) : setSinPago(n))}>
                      {n.exentaDeCobro ? <PaidIcon fontSize="small" /> : <CardGiftcardIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={n.ownerID ? 'Cambiar contraseña del dueño' : 'Sin usuario de acceso'}>
                    <span>
                      <IconButton size="small" disabled={!n.ownerID} onClick={() => setPassword(n)}>
                        <KeyIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {n.empresaId && (
                    <Tooltip title={n.estadoValidacion === 'APROBADO' ? 'Ver validación' : 'Validar documentos'}>
                      <IconButton size="small"
                        color={n.estadoValidacion === 'APROBADO' ? 'default' : 'warning'}
                        onClick={() => navigate(`/admin/negocios/${n.empresaId}/validar`,
                          { state: { from: '/admin/negocios' } })}>
                        <AssignmentIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* Pendiente: aprobar o rechazar. Es el unico estado desde el
                      que se puede aprobar, para no reabrir por error uno que se
                      suspendio o rechazo a proposito. */}
                  {n.estado === 'pendiente_aprobacion' && <>
                    <Tooltip title="Aprobar">
                      <IconButton size="small" color="success"
                        onClick={() => aprobar(n.tenantID, n.nombre)}>
                        <CheckCircleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Rechazar">
                      <IconButton size="small" color="error"
                        onClick={() => rechazar(n.tenantID)}>
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>}

                  {n.estado === 'suspendido'
                    ? <Tooltip title="Reactivar"><IconButton size="small" onClick={() => reactivar(n.tenantID)}><PlayCircleIcon fontSize="small" /></IconButton></Tooltip>
                    : <Tooltip title="Suspender"><IconButton size="small" onClick={() => suspender(n.tenantID)}><BlockIcon fontSize="small" /></IconButton></Tooltip>}

                  {/* D-27: todo lo que se le ha hecho a este negocio. */}
                  <Tooltip title="Historial de auditoría">
                    <IconButton size="small" onClick={() => setHistorial(n)}>
                      <HistoryIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div" count={data.total} page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={size}
          onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Card>

      <Dialog open={!!detalle} onClose={() => setDetalle(null)} maxWidth="sm" fullWidth>
        {detalle && <>
          <DialogTitle>{detalle.nombre}</DialogTitle>
          <DialogContent dividers>
            <Field k="Slug" v={detalle.slug} />
            <Field k="Estado" v={detalle.estado} />
            <Field k="Correo" v={detalle.email || '—'} />
            <Field k="Teléfono" v={detalle.telefono || '—'} />
            <Field k="Tipo negocio" v={detalle.tipoNegocio || '—'} />
            <Field k="Fecha creación" v={fechaHoraLocal(detalle.fechaCreacion)} />
            {detalle.motivoSuspension && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Suspendido: {detalle.motivoSuspension}
                <br />
                <Typography variant="caption">{fechaHoraLocal(detalle.fechaSuspension)}</Typography>
              </Alert>
            )}
            {detalle.sucursal && <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Sucursal (tienda)</Typography>
              <Field k="Nombre"     v={detalle.sucursal.nombre} />
              <Field k="Código"     v={detalle.sucursal.codigo} />
              <Field k="Dirección"  v={detalle.sucursal.direccion || '—'} />
              <Field k="Teléfono"   v={detalle.sucursal.telefono || '—'} />
              <Field k="Categoría"  v={detalle.sucursal.categoria || '—'} />
              <Field k="Activa"     v={detalle.sucursal.activa ? 'Sí' : 'No'} />
              {(detalle.sucursal.latitud != null && detalle.sucursal.longitud != null) && (
                <Stack direction="row" spacing={1} sx={{ py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>Ubicación</Typography>
                  <Typography
                    component="a"
                    href={`https://www.google.com/maps/?q=${detalle.sucursal.latitud},${detalle.sucursal.longitud}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    sx={{ color: 'primary.main', textDecoration: 'underline' }}
                  >
                    Abrir en Google Maps ({detalle.sucursal.latitud.toFixed(5)}, {detalle.sucursal.longitud.toFixed(5)})
                  </Typography>
                </Stack>
              )}
            </>}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Usuarios del tenant ({detalle.usuarios?.length || 0})</Typography>
            {detalle.usuarios?.map((u) => (
              <Stack key={u.usuarioTenantID} direction="row" spacing={1} sx={{ py: 0.3 }}>
                <Chip size="small" label={u.rol} />
                <Typography variant="body2">{u.entidadTipo} #{u.entidadID}</Typography>
              </Stack>
            ))}
          </DialogContent>
          <DialogActions><Button onClick={() => setDetalle(null)}>Cerrar</Button></DialogActions>
        </>}
      </Dialog>

      <DialogActivarSinPago
        abierto={sinPago !== null}
        negocio={sinPago}
        onCerrar={() => setSinPago(null)}
        onHecho={(m) => { setAviso(m); cargar(); }}
      />

      <DialogHistorialAuditoria
        abierto={historial !== null}
        entidad="Negocio"
        entidadId={historial?.tenantID}
        titulo={historial?.nombre}
        onCerrar={() => setHistorial(null)}
      />

      <DialogCambiarPassword
        abierto={password !== null}
        aspNetUserId={password?.ownerID}
        nombre={password?.nombre}
        detalle={[password?.email, password?.telefono].filter(Boolean).join(' · ')}
        onCerrar={() => setPassword(null)}
        onCambiada={setAviso}
      />

      <Snackbar
        open={!!aviso} autoHideDuration={6000} onClose={() => setAviso('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setAviso('')}>{aviso}</Alert>
      </Snackbar>
    </Box>
  );
}

const Field = ({ k, v }) => (
  <Stack direction="row" spacing={1} sx={{ py: 0.5 }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>{k}</Typography>
    <Typography variant="body2">{v}</Typography>
  </Stack>
);
